import { useFormik } from "formik";
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../loading/spinner";

// ============================================
// LOCALSTORAGE HELPERS
// ============================================

const getAppointments = () => {
  try {
    return JSON.parse(localStorage.getItem("appointments")) || [];
  } catch {
    return [];
  }
};

const saveAppointments = (appointments) => {
  localStorage.setItem("appointments", JSON.stringify(appointments));
};

const getClinics = () => {
  try {
    return JSON.parse(localStorage.getItem("clinics")) || [];
  } catch {
    return [];
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

const EditScheduleAppointment = ({ detail, reset }) => {
  const [loading, setLoading] = useState(false);
  const [clinics, setClinics] = useState([]);

  // Load clinics on mount
  useEffect(() => {
    const activeClinics = getClinics().filter((c) => c.clinicStatus !== "DELETED");
    setClinics(activeClinics);
  }, []);

  // Handle clinic selection
  const handleClinicChange = (e) => {
    const clinicId = e.target.value;
    const selectedClinic = clinics.find((c) => c.id === parseInt(clinicId, 10));
    formik.setValues({
      ...formik.values,
      clinicId: clinicId,
      clinicLocation: selectedClinic?.location || "",
    });
  };

  const formik = useFormik({
    initialValues: {
      name: detail?.name || "",
      abhaNumber: detail?.abhaNumber || "",
      age: detail?.age || "",
      contact: detail?.contact || "",
      gender: detail?.gender || "",
      appointmentDate: detail?.appointmentDate || "",
      appointmentTime: detail?.appointmentTime || "",
      clinicId: detail?.clinicId || "",
      clinicLocation: detail?.clinicLocation || "",
      guardianName: detail?.guardianName || "",
      address: detail?.address || "",
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object().shape({
      name: Yup.string().required("Name is required"),
      abhaNumber: Yup.string().optional(),
      age: Yup.string().required("Age is required"),
      contact: Yup.string()
        .matches(/^\d{10}$/, "Must be 10 digits")
        .required("Contact is required"),
      gender: Yup.string().required("Gender is required"),
      appointmentDate: Yup.string().required("Appointment date is required"),
      appointmentTime: Yup.string().required("Appointment time is required"),
      clinicId: Yup.string().required("Clinic is required"),
      clinicLocation: Yup.string().required("Clinic location is required"),
      guardianName: Yup.string().optional(),
      address: Yup.string().optional(),
    }),
    onSubmit: (values) => {
      handleEditAppointment(values);
    },
  });

  const handleEditAppointment = (values) => {
    if (!detail || !detail.id) {
      toast.error("Invalid appointment data.");
      return;
    }

    setLoading(true);

    // Get all appointments
    const appointments = getAppointments();

    // Find the appointment to update
    const index = appointments.findIndex((apt) => apt.id === detail.id);

    if (index === -1) {
      toast.error("Appointment not found.");
      setLoading(false);
      return;
    }

    // Update the appointment
    const updatedAppointment = {
      ...appointments[index],
      name: values.name,
      abhaNumber: values.abhaNumber,
      age: values.age,
      contact: values.contact,
      gender: values.gender,
      appointmentDate: values.appointmentDate,
      appointmentTime: values.appointmentTime,
      clinicId: parseInt(values.clinicId, 10),
      clinicLocation: values.clinicLocation,
      guardianName: values.guardianName,
      address: values.address,
      updatedAt: new Date().toISOString(),
    };

    appointments[index] = updatedAppointment;
    saveAppointments(appointments);

    toast.success("Appointment updated successfully!");
    setLoading(false);

    // Close modal
    reset();
  };

  return (
    <div
      id="login-popup"
      tabIndex="-1"
      className="bg-black/50 overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 h-full items-center justify-center flex"
    >
      <div className="relative p-4 w-full max-w-md h-full md:h-auto">
        <div className="relative bg-white rounded-lg shadow">
          <button
            type="button"
            className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center popup-close"
            onClick={reset}
          >
            <svg
              aria-hidden="true"
              className="w-5 h-5"
              fill="#c6c7c7"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            <span className="sr-only">Close popup</span>
          </button>
          <div className="p-5">
            <div className="text-center">
              <p className="mb-3 text-2xl font-semibold leading-5 text-slate-900">
                Edit Appointment
              </p>
            </div>

            {/* Form */}
            <form onSubmit={formik.handleSubmit}>
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2 sm:col-span-1">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    id="name"
                    placeholder="Full Name"
                    className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="text-red-500 text-sm">{formik.errors.name}</div>
                  )}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label
                    htmlFor="abhaNumber"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    ABHA Number
                  </label>
                  <input
                    type="text"
                    name="abhaNumber"
                    value={formik.values.abhaNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    id="abhaNumber"
                    placeholder="ABHA Number"
                    className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  {formik.touched.abhaNumber && formik.errors.abhaNumber && (
                    <div className="text-red-500 text-sm">{formik.errors.abhaNumber}</div>
                  )}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label
                    htmlFor="age"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Age
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formik.values.age}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    id="age"
                    placeholder="Age"
                    className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  {formik.touched.age && formik.errors.age && (
                    <div className="text-red-500 text-sm">{formik.errors.age}</div>
                  )}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label
                    htmlFor="contact"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Phone Number
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="contact"
                    value={formik.values.contact}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    id="contact"
                    placeholder="Mobile Number"
                    className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  {formik.touched.contact && formik.errors.contact && (
                    <div className="text-red-500 text-sm">{formik.errors.contact}</div>
                  )}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label
                    htmlFor="gender"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Gender
                  </label>
                  <select
                    className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                    id="gender"
                    name="gender"
                    value={formik.values.gender}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">Select Gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                  {formik.touched.gender && formik.errors.gender && (
                    <div className="text-red-500 text-sm">{formik.errors.gender}</div>
                  )}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label
                    htmlFor="appointmentDate"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Schedule Date
                  </label>
                  <input
                    type="date"
                    name="appointmentDate"
                    value={formik.values.appointmentDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    id="appointmentDate"
                    className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  {formik.touched.appointmentDate && formik.errors.appointmentDate && (
                    <div className="text-red-500 text-sm">{formik.errors.appointmentDate}</div>
                  )}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label
                    htmlFor="guardianName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Guardian Name
                  </label>
                  <input
                    type="text"
                    name="guardianName"
                    value={formik.values.guardianName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    id="guardianName"
                    placeholder="Guardian Name"
                    className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  {formik.touched.guardianName && formik.errors.guardianName && (
                    <div className="text-red-500 text-sm">{formik.errors.guardianName}</div>
                  )}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label
                    htmlFor="address"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    id="address"
                    placeholder="Address"
                    className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  {formik.touched.address && formik.errors.address && (
                    <div className="text-red-500 text-sm">{formik.errors.address}</div>
                  )}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label
                    htmlFor="clinicId"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Select Clinic
                  </label>
                  <select
                    id="clinicId"
                    name="clinicId"
                    value={formik.values.clinicId}
                    onChange={handleClinicChange}
                    onBlur={formik.handleBlur}
                    className="w-full py-2 px-4 text-black border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Select Clinic</option>
                    {clinics.map((clinic) => (
                      <option key={clinic.id} value={clinic.id}>
                        {clinic.location?.substring(0, 20) || "Clinic"}
                      </option>
                    ))}
                  </select>
                  {formik.touched.clinicId && formik.errors.clinicId && (
                    <div className="text-red-500 text-sm">{formik.errors.clinicId}</div>
                  )}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label
                    htmlFor="appointmentTime"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Schedule Time
                  </label>
                  <input
                    type="time"
                    name="appointmentTime"
                    value={formik.values.appointmentTime}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    id="appointmentTime"
                    className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  {formik.touched.appointmentTime && formik.errors.appointmentTime && (
                    <div className="text-red-500 text-sm">{formik.errors.appointmentTime}</div>
                  )}
                </div>
              </div>

              <div className="mt-8">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-green-500 hover:bg-blue-600 text-white font-medium py-2 rounded-lg focus:outline-none disabled:opacity-50"
                >
                  {loading ? "Updating..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {loading && <Spinner />}
    </div>
  );
};

export default EditScheduleAppointment;